/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import { useMemo, useCallback, useRef } from '@web-stories-wp/react';
import styled from 'styled-components';
import { __ } from '@web-stories-wp/i18n';
import { DropDown, NumericInput } from '@web-stories-wp/design-system';

/**
 * Internal dependencies
 */
import { useFont } from '../../../../app/font';
import {
  collectTextFontUsage,
  parseInlineFontSize,
  resolveTextRuns,
} from '../../../../elements/text/utils/mixedTypography';
import { getHTMLFormatters } from '../../../richText/htmlManipulation';
import clamp from '../../../../utils/clamp';
import { MULTIPLE_VALUE, MULTIPLE_DISPLAY_VALUE } from '../../../../constants';
import { Row, usePresubmitHandler } from '../../../form';
import {
  focusStyle,
  getCommonValue,
  inputContainerStyleOverride,
} from '../../shared';
import useRichTextFormatting from './useRichTextFormatting';
import getFontWeights from './getFontWeights';
import FontPicker from './fontPicker';

const MIN_MAX = {
  FONT_SIZE: {
    MIN: 8,
    MAX: 800,
  },
};

const Space = styled.div`
  flex: 0 0 10px;
`;

const StyledNumericInput = styled(NumericInput)`
  flex-basis: 80px;
  text-align: center;
`;

const StyledDropDown = styled(DropDown)`
  background-color: transparent;
`;

export function getEffectiveFontFamily(selectedElements) {
  const effectiveFontUsage = selectedElements.flatMap((element) => {
    const usage = collectTextFontUsage(element);
    return usage.length ? usage : [{ font: element.font }];
  });

  return effectiveFontUsage.length
    ? getCommonValue(effectiveFontUsage, ({ font }) => font?.family)
    : undefined;
}

export function getEffectiveFontSize(selectedElements) {
  const effectiveSizes = selectedElements.flatMap((element) =>
    resolveTextRuns(element).map(({ fontSize }) => fontSize)
  );
  return effectiveSizes.length
    ? getCommonValue(effectiveSizes, (fontSize) => fontSize)
    : undefined;
}

export function getElementWideFontSizeUpdate(element, requestedFontSize) {
  const nextFontSize = parseInlineFontSize(requestedFontSize);
  if (nextFontSize === null) {
    return {};
  }
  const hasInlineSizeOverrides = resolveTextRuns(element).some(
    ({ fontSizeOverride }) => fontSizeOverride
  );
  return {
    fontSize: nextFontSize,
    ...(hasInlineSizeOverrides
      ? {
          content: getHTMLFormatters().setFontSize(element.content, {
            fontSize: nextFontSize,
            defaultFontSize: nextFontSize,
          }),
        }
      : {}),
  };
}

function FontControls({
  selectedElements,
  pushUpdate,
  fontDropdownRef,
  highlightStylesOverride,
}) {
  const elementFontFamily = useMemo(
    () => getEffectiveFontFamily(selectedElements),
    [selectedElements]
  );
  const effectiveFontSize = useMemo(
    () => getEffectiveFontSize(selectedElements),
    [selectedElements]
  );
  const {
    textInfo: {
      fontWeight,
      isItalic,
      fontFamily: selectedFontFamily,
      fontSize: selectedFontSize,
    },
    handlers: {
      handleSelectFontWeight,
      handleSetFontFamily,
      handleSetFontSize,
    },
    isEditingMixedTypography,
  } = useRichTextFormatting(selectedElements, pushUpdate);
  const fontFamily = isEditingMixedTypography
    ? selectedFontFamily
    : elementFontFamily;
  const fontSize = isEditingMixedTypography
    ? selectedFontSize
    : effectiveFontSize;
  const hasOutOfRangeFontSize =
    Number.isFinite(fontSize) &&
    fontSize !== clamp(fontSize, MIN_MAX.FONT_SIZE);
  const fontSizeEdited = useRef(false);

  const {
    fonts = [],
    maybeEnqueueFontStyle,
    getFontByName,
  } = useFont(
    ({
      actions: { maybeEnqueueFontStyle, getFontByName },
      state: { fonts },
    }) => ({
      maybeEnqueueFontStyle,
      getFontByName,
      fonts,
    })
  );

  const fontWeights = useMemo(
    () =>
      fontFamily === MULTIPLE_VALUE
        ? null
        : getFontWeights(getFontByName(fontFamily)),
    [getFontByName, fontFamily]
  );
  const fontStyle = isItalic ? 'italic' : 'normal';

  const handleFontWeightPickerChange = useCallback(
    async (evt, value) => {
      await maybeEnqueueFontStyle(
        selectedElements.flatMap((element) =>
          collectTextFontUsage(element).map((usage) => ({
            ...usage,
            fontStyle,
            fontWeight: parseInt(value),
          }))
        )
      );
      handleSelectFontWeight(value);
    },
    [fontStyle, handleSelectFontWeight, maybeEnqueueFontStyle, selectedElements]
  );

  usePresubmitHandler(({ fontSize: newFontSize }, updatedProperties = {}) => {
    if (!Object.prototype.hasOwnProperty.call(updatedProperties, 'fontSize')) {
      return {};
    }
    return { fontSize: clamp(newFontSize, MIN_MAX.FONT_SIZE) };
  }, []);

  return (
    <>
      {fonts && (
        <Row>
          <FontPicker
            ref={fontDropdownRef}
            selectedElements={selectedElements}
            pushUpdate={pushUpdate}
            highlightStylesOverride={highlightStylesOverride}
            fontFamily={fontFamily}
            fontWeight={fontWeight}
            isItalic={isItalic}
            isEditingMixedTypography={isEditingMixedTypography}
            handleSetFontFamily={handleSetFontFamily}
          />
        </Row>
      )}
      <Row>
        {fontWeights && (
          <>
            <StyledDropDown
              ariaLabel={__('Font weight', 'web-stories')}
              placeholder={MULTIPLE_DISPLAY_VALUE}
              options={fontWeights}
              selectedValue={MULTIPLE_VALUE === fontWeight ? '' : fontWeight}
              onMenuItemClick={handleFontWeightPickerChange}
              selectButtonStylesOverride={focusStyle}
            />
            <Space />
          </>
        )}
        <StyledNumericInput
          aria-label={__('Font size', 'web-stories')}
          isFloat
          value={fontSize}
          onChangeCapture={() => {
            // Include temporary numeric drafts that do not call onChange yet.
            fontSizeEdited.current = true;
          }}
          onChange={(evt, value) => {
            fontSizeEdited.current = true;
            if (isEditingMixedTypography) {
              if (typeof value === 'number' && Number.isFinite(value)) {
                handleSetFontSize(clamp(value, MIN_MAX.FONT_SIZE));
              }
              return;
            }
            if (typeof value === 'number' && Number.isFinite(value)) {
              const nextFontSize = clamp(value, MIN_MAX.FONT_SIZE);
              pushUpdate(
                (element) =>
                  getElementWideFontSizeUpdate(element, nextFontSize),
                false
              );
            }
          }}
          onBlur={(evt, value) => {
            const didEditFontSize = fontSizeEdited.current;
            fontSizeEdited.current = false;
            // Merely leaving an existing out-of-range size is not a size edit.
            if (
              !didEditFontSize &&
              hasOutOfRangeFontSize &&
              value === fontSize
            ) {
              return;
            }
            if (isEditingMixedTypography) {
              if (typeof value === 'number' && Number.isFinite(value)) {
                handleSetFontSize(clamp(value, MIN_MAX.FONT_SIZE));
              }
              return;
            }
            if (typeof value === 'number' && Number.isFinite(value)) {
              const nextFontSize = clamp(value, MIN_MAX.FONT_SIZE);
              pushUpdate(
                (element) =>
                  getElementWideFontSizeUpdate(element, nextFontSize),
                true
              );
            }
          }}
          // Let NumericInput retain the authored value on an unchanged blur;
          // intentional edits still use the authoring clamp in the handlers.
          min={
            hasOutOfRangeFontSize
              ? Math.min(fontSize, MIN_MAX.FONT_SIZE.MIN)
              : MIN_MAX.FONT_SIZE.MIN
          }
          max={
            hasOutOfRangeFontSize
              ? Math.max(fontSize, MIN_MAX.FONT_SIZE.MAX)
              : MIN_MAX.FONT_SIZE.MAX
          }
          isIndeterminate={MULTIPLE_VALUE === fontSize}
          placeholder={
            MULTIPLE_VALUE === fontSize ? MULTIPLE_DISPLAY_VALUE : null
          }
          containerStyleOverride={inputContainerStyleOverride}
        />
      </Row>
    </>
  );
}

FontControls.propTypes = {
  selectedElements: PropTypes.array.isRequired,
  pushUpdate: PropTypes.func.isRequired,
  fontDropdownRef: PropTypes.func,
  highlightStylesOverride: PropTypes.array,
};

export default FontControls;
